import path from 'node:path';
import pc from 'picocolors';
import { ApiClient, deriveBaseDomain } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { git, gitAvailable } from '../lib/git.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, note, ok, type Io } from '../output.js';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const POLL_MS = 5_000;
const DEPLOY_TIMEOUT_MS = 15 * 60 * 1000;

const TERMINAL_FAIL = new Set(['FAILURE', 'TIMEOUT', 'CANCELLED', 'INTERNAL_ERROR', 'EXPIRED']);

interface DeployAccepted {
  ok: boolean;
  buildId: string;
  commitSha: string;
}
export interface DeployRecord {
  id?: string;
  status?: string;
  commitSha?: string;
  /** Failure summary for FAILED builds, when the platform captured one. */
  error?: string;
  /** 'rollback' when this row re-released a previous build instead of building. */
  kind?: string;
}
interface DeploysResponse {
  slug: string;
  deploys: DeployRecord[];
}

/**
 * Poll an app's deploy history until the given build reaches a terminal
 * state. SUCCESS returns; any failure state exits via fail(). Matches by
 * build id, and ADDITIONALLY by commit sha when one is passed — rollbacks
 * must NOT pass a sha (the original deploy of the same sha would match and
 * report instant false success).
 */
export async function waitForDeploy(
  client: ApiClient,
  io: Io,
  slug: string,
  target: { buildId: string; commitSha?: string },
  opts: { timeoutMs?: number } = {},
): Promise<void> {
  const deadline = Date.now() + (opts.timeoutMs ?? DEPLOY_TIMEOUT_MS);
  for (;;) {
    if (Date.now() > deadline) {
      fail(io, 'Deploy timed out after 15 minutes. See the full build log in your app console → Deploys.', 1, 'DEPLOY_TIMEOUT');
    }
    await sleep(POLL_MS);
    let mine: DeployRecord | undefined;
    try {
      const { deploys } = await client.get<DeploysResponse>(`/builder-api/apps/${slug}/deploys?limit=10`);
      mine = deploys.find(
        (d) => d.id === target.buildId || (target.commitSha != null && d.commitSha === target.commitSha),
      );
    } catch {
      continue; // transient; keep polling
    }
    const status = mine?.status;
    if (status === 'SUCCESS') return;
    if (status && TERMINAL_FAIL.has(status)) {
      const detail = mine?.error?.trim()
        ? `\n${mine.error
            .trim()
            .split('\n')
            .map((line) => `  ${line}`)
            .join('\n')}\n`
        : ' ';
      fail(
        io,
        `Build ${status.toLowerCase()}.${detail}See the full build log in your app console → Deploys.`,
        1,
        'DEPLOY_FAILED',
      );
    }
  }
}

/**
 * Guard that this folder is a pushable git checkout: git installed, inside a
 * work tree, with an `origin` remote. Exits via fail() otherwise. Shared by
 * `ghosty deploy` and `ghosty preview`.
 */
export function ensureGitReady(io: Io, dir: string): void {
  if (!gitAvailable()) {
    fail(io, 'git is not installed. Install git, then run `ghosty deploy` again.', 1, 'NO_GIT');
  }
  if (!git(['rev-parse', '--is-inside-work-tree'], dir)?.ok) {
    fail(io, 'This folder is not a git repo yet. Run `ghosty provision` first (it wires your repo).', 1, 'NO_REPO');
  }
  if (!git(['remote', 'get-url', 'origin'], dir)?.ok) {
    fail(io, 'No git remote "origin". Run `ghosty provision` to connect your app to its repo.', 1, 'NO_REMOTE');
  }
}

/**
 * Stage everything and commit it as `message` (with the builder's own git
 * identity). A clean tree is fine — the current commit ships as-is. Exits via
 * fail() when the commit itself fails (usually a missing git identity).
 */
export function autoCommit(io: Io, dir: string, message: string): void {
  git(['add', '-A'], dir);
  const dirty = (git(['status', '--porcelain'], dir)?.output ?? '').length > 0;
  if (!dirty) {
    note(io, 'No changes to commit — deploying the current commit.');
    return;
  }
  const commit = git(['commit', '-m', message], dir);
  if (!commit?.ok) {
    fail(
      io,
      `git commit failed: ${commit?.output ?? 'unknown error'}. Set your git identity ` +
        '(`git config user.email "you@company.com"` and `git config user.name "Your Name"`) and retry.',
      1,
      'COMMIT_FAILED',
    );
  }
}

/**
 * `ghosty deploy` — ship the current app. Commits + pushes the working tree to
 * the company repo with the builder's OWN git identity, then asks the platform
 * to build & deploy that commit (source build — no external build↔GitHub
 * connection). The code stays in the company's org; the platform only reads it.
 */
export async function runDeploy(options: {
  dir?: string;
  message?: string;
  json?: boolean;
  adminUrl?: string;
}): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');

  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  let config;
  try {
    config = readAppConfig(dir);
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }
  const slug = config.slug;

  // ---- git: commit + push the working tree to the company repo --------------
  ensureGitReady(io, dir);
  autoCommit(io, dir, options.message?.trim() || 'Deploy');

  note(io, 'Pushing to your repo…');
  const push = git(['push', 'origin', 'HEAD:main'], dir);
  if (!push?.ok) {
    fail(
      io,
      `git push failed: ${push?.output ?? 'unknown error'}. Make sure your GitHub account can ` +
        'push to this repo (accept the org invite from `ghosty login`, or pull remote changes first).',
      1,
      'PUSH_FAILED',
    );
  }
  const sha = git(['rev-parse', 'HEAD'], dir)?.output.trim() ?? 'main';

  // ---- ask the platform to build & deploy that commit -----------------------
  const client = new ApiClient(resolved.adminUrl, resolved.profile.token);
  let accepted: DeployAccepted;
  try {
    accepted = await client.post<DeployAccepted>(`/builder-api/apps/${slug}/deploy`, { ref: sha });
  } catch (err) {
    fail(io, (err as Error).message, 1, 'DEPLOY_REJECTED');
  }
  if (io.json) emitJson({ event: 'building', slug, buildId: accepted.buildId, commitSha: accepted.commitSha });
  else note(io, `Building ${slug} @ ${accepted.commitSha.slice(0, 7)}…`);

  // ---- poll the build to completion -----------------------------------------
  await waitForDeploy(client, io, slug, {
    buildId: accepted.buildId,
    commitSha: accepted.commitSha,
  });

  // ---- done ------------------------------------------------------------------
  const baseDomain = await (async () => {
    try {
      return deriveBaseDomain(resolved.adminUrl, await client.fetchConfig());
    } catch {
      return deriveBaseDomain(resolved.adminUrl);
    }
  })();
  const appUrl = `https://${slug}.${baseDomain}`;

  if (io.json) {
    emitJson({ ok: true, slug, commitSha: accepted.commitSha, appUrl });
    return;
  }
  human(io);
  ok(io, `${pc.bold(config.name)} deployed.`);
  human(io, `  Live: ${pc.bold(appUrl)}`);
  human(io, `  Commit: ${accepted.commitSha.slice(0, 7)} (in your repo)`);
  human(io);
}
